import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { TabPanel } from '../../../../../components/tabs';
import { IDetailsTabPanel } from './interfaces';
import { Paper, Table, TableBody, TableContainer } from '@mui/material';
import {
  ValueActionRow,
  ValueAgentRow,
  ValueChipRow,
  ValueDateRow,
  ValueRow,
} from '../../../../../components/table';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { UpsertContactDialog } from '../../../dialogs/UpsertContactDialog';
import { DeleteContactDialog } from '../../../dialogs/DeleteContactDialog';
import { DeleteAction } from '../../../../../components/FloatingActionsButtons/components/DeleteAction';
import { EditAction } from '../../../../../components/FloatingActionsButtons/components/EditAction';
import { FloatingActionsButtons } from '../../../../../components/FloatingActionsButtons';
import { enqueueSnackbar } from 'notistack';
import { useDeleteContact } from '../../../../../reactQuery/contacts';

export const DetailsTabPanel: React.FC<IDetailsTabPanel> = ({
  index,
  value,
  contact,
}): JSX.Element => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);

  const { mutate: deleteContact, isLoading: isDeletingContact } =
    useDeleteContact();

  const onDeleteContact = (): void => {
    deleteContact(contact.id, {
      onSuccess: () => {
        enqueueSnackbar(t('contacts:prompts.deleteContact.success'), {
          variant: 'success',
        });
        navigate('/contacts');
      },
      onError: () => {
        enqueueSnackbar(t('contacts:prompts.deleteContact.failed'), {
          variant: 'error',
        });
      },
    });
  };

  return (
    <TabPanel value={value} index={index}>
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            <ValueRow
              label={t('entities:common.firstName')}
              value={contact.firstName}
            />
            <ValueRow
              label={t('entities:common.lastName')}
              value={contact.lastName || undefined}
            />
            <ValueActionRow
              label={t('entities:common.email')}
              value={contact.email || undefined}
              actionLabel={t('contacts:buttons.mail')}
              actionIcon={<EmailIcon />}
              onAction={() =>
                (window.location.href = `mailto:${contact.email}`)
              }
            />
            <ValueActionRow
              label={t('entities:contact.phonenumber')}
              value={contact.phonenumber || undefined}
              actionLabel={t('contacts:buttons.call')}
              actionIcon={<PhoneIcon />}
              onAction={() =>
                (window.location.href = `tel:${contact.phonenumber}`)
              }
            />
            <ValueChipRow
              label={t('entities:contact.jobFunction')}
              value={contact.jobFunctions?.map(
                (jobFunction) => jobFunction.name
              )}
            />
            <ValueChipRow
              label={t('entities:contact.clients')}
              value={contact.clients?.map((client) => client.name)}
            />

            <ValueRow
              label={t('entities:contact.contactInterval')}
              value={
                contact.contactInterval
                  ? t('words.month', { count: contact.contactInterval / 30 })
                  : t('forms:createContact.fields.contactInterval.noInterval')
              }
            />

            <ValueAgentRow
              agent={contact.createdBy}
              label={t('entities:common.createdBy')}
            />

            <ValueDateRow
              date={contact.createdAt}
              label={t('entities:common.createdAt')}
            />

            <ValueDateRow
              date={contact.updatedAt}
              label={t('entities:common.updatedAt')}
            />
          </TableBody>
        </Table>
      </TableContainer>

      <FloatingActionsButtons>
        <DeleteAction
          onClick={() => {
            setShowDeleteDialog(true);
          }}
        />
        <EditAction
          onClick={() => {
            setShowEditDialog(true);
          }}
        />
      </FloatingActionsButtons>

      <UpsertContactDialog
        contact={contact}
        show={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      />
      <DeleteContactDialog
        show={showDeleteDialog}
        contactName={`${contact.firstName} ${contact.lastName}`}
        disabled={isDeletingContact}
        onConfirm={onDeleteContact}
        onClose={() => setShowDeleteDialog(false)}
      />
    </TabPanel>
  );
};
